import React, { useState, useEffect } from "react";
import SmsConsentAPI from "../../services/SmsConsentAPI";
import UserAPI from "../../services/UserAPI";
import UserSettingsAPI from "../../services/UserSettingsAPI";
import { showToastWithAction } from "../../utils/ToastWithAction";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCheckCircle,
  faExclamationCircle,
  faSpinner,
} from "@fortawesome/free-solid-svg-icons";
import { useTranslation } from "../../../../i18n/LanguageContext";
import "./css/PreferenceSettings.css";
import LoadingSpinner from "../../common/LoadingSpinner";

const NotificationCommon = () => {
  const { t } = useTranslation();
  const [smsConsentStatus, setSmsConsentStatus] = useState(null);
  const [userPhone, setUserPhone] = useState(null);
  const [userInfo, setUserInfo] = useState(null);
  const [isLoadingConsent, setIsLoadingConsent] = useState(true);
  const [isOptingOut, setIsOptingOut] = useState(false);
  const [isOptingIn, setIsOptingIn] = useState(false);
  const [optOutMessage, setOptOutMessage] = useState(null);
  const [optInMessage, setOptInMessage] = useState(null);
  const [showVerificationInput, setShowVerificationInput] = useState(false);
  const [verificationCode, setVerificationCode] = useState("");
  const [isVerifying, setIsVerifying] = useState(false);
  const [verificationMessage, setVerificationMessage] = useState(null);

  // Alert preferences state
  const [alertLevel, setAlertLevel] = useState("info");

  // Email notification toggle states
  const [emailSecurityAlerts, setEmailSecurityAlerts] = useState(true);
  const [emailTrainingReminders, setEmailTrainingReminders] = useState(true);
  const [emailWeeklyDigest, setEmailWeeklyDigest] = useState(false);

  // Push / Desktop notifications
  const [pushNotificationsEnabled, setPushNotificationsEnabled] =
    useState(false);

  // Load settings from backend on mount
  useEffect(() => {
    const loadSettings = async () => {
      try {
        const data = await UserSettingsAPI.getSettings();
        if (data) {
          setEmailSecurityAlerts(data.emailSecurityAlerts ?? true);
          setEmailTrainingReminders(data.emailTrainingReminders ?? true);
          setEmailWeeklyDigest(data.emailWeeklyDigest ?? false);
          setPushNotificationsEnabled(data.pushNotificationsEnabled ?? false);
          setAlertLevel(data.inappAlertLevel || "info");
        }
      } catch (err) {
        console.error("NotificationSettings: Error loading settings", err);
        // Fallback to localStorage
        setAlertLevel(localStorage.getItem("alertLevel") || "info");
        setEmailSecurityAlerts(
          localStorage.getItem("emailSecurityAlerts") !== "false",
        );
        setEmailTrainingReminders(
          localStorage.getItem("emailTrainingReminders") !== "false",
        );
        setEmailWeeklyDigest(
          localStorage.getItem("emailWeeklyDigest") === "true",
        );
        setPushNotificationsEnabled(
          localStorage.getItem("pushNotificationsEnabled") === "true",
        );
      }
    };
    loadSettings();
  }, []);

  // Generic toggle handler that saves to backend
  const handleToggle = async (field, currentValue, setter) => {
    const newValue = !currentValue;
    setter(newValue);
    try {
      await UserSettingsAPI.updateSettings({ [field]: newValue });
    } catch (err) {
      console.error(`Failed to save ${field}`, err);
      setter(currentValue); // revert on failure
    }
  };

  // Handle alert level change - also save to backend
  const handleAlertLevelChange = async (e) => {
    const newLevel = e.target.value;
    const oldLevel = alertLevel;
    setAlertLevel(newLevel);
    localStorage.setItem("alertLevel", newLevel);
    window.dispatchEvent(
      new CustomEvent("alertLevelChanged", {
        detail: { alertLevel: newLevel },
      }),
    );
    try {
      await UserSettingsAPI.updateSettings({ inappAlertLevel: newLevel });
    } catch (err) {
      console.error("Failed to save alert level", err);
      setAlertLevel(oldLevel);
      localStorage.setItem("alertLevel", oldLevel);
    }
  };

  // Load user phone and SMS consent status on component mount
  useEffect(() => {
    const loadUserAndConsentStatus = async () => {
      try {
        // Get current user info to get phone number
        const userData = await UserAPI.getCurrentUser();
        console.log("[NotificationSettings] User data loaded:", userData);
        console.log(
          "[NotificationSettings] Available fields:",
          Object.keys(userData || {}),
        );

        // Check multiple possible phone number fields
        const phoneNumber =
          userData?.phone || userData?.phoneNumber || userData?.mobilePhone;
        console.log("[NotificationSettings] Phone number found:", phoneNumber);

        if (userData && phoneNumber) {
          setUserInfo(userData);
          setUserPhone(phoneNumber);
          console.log("[NotificationSettings] Using phone:", phoneNumber);

          // Check SMS consent status
          try {
            const consentStatus =
              await SmsConsentAPI.getSmsConsentByPhone(phoneNumber);
            console.log(
              "[NotificationSettings] Consent status:",
              consentStatus,
            );
            setSmsConsentStatus(consentStatus);
          } catch (error) {
            console.log(
              "[NotificationSettings] No SMS consent found for user, error:",
              error,
            );
            setSmsConsentStatus(null);
          }
        } else {
          console.log(
            "[NotificationSettings] No phone number found for user. User data:",
            userData,
          );
          setUserInfo(userData);
          setUserPhone(null);
        }
      } catch (error) {
        console.error("Error loading user information:", error);
      } finally {
        setIsLoadingConsent(false);
      }
    };

    loadUserAndConsentStatus();
  }, []);

  // Handle SMS opt-out
  const handleSmsOptOut = async () => {
    if (!userPhone) {
      setOptOutMessage({
        type: "error",
        text: t("notifications.sms.noPhoneError"),
      });
      return;
    }

    showToastWithAction(
      t("notifications.sms.optOutConfirm"),
      async () => {
        setIsOptingOut(true);
        setOptOutMessage(null);
        setOptInMessage(null);
        setVerificationMessage(null);

        try {
          const response = await SmsConsentAPI.optOutSmsConsent(userPhone);

          if (response && response.success) {
            setOptOutMessage({
              type: "success",
              text: t("notifications.sms.optedOutSuccess"),
            });

            // Update consent status
            setSmsConsentStatus((prev) => ({
              ...prev,
              hasConsent: false,
              verified: false,
              status: "OPTED_OUT",
            }));
          } else {
            setOptOutMessage({
              type: "error",
              text: t("notifications.sms.optOutFailed"),
            });
          }
        } catch (error) {
          console.error("Error opting out of SMS:", error);
          setOptOutMessage({
            type: "error",
            text: t("notifications.sms.optOutError"),
          });
        } finally {
          setIsOptingOut(false);
        }
      },
      5,
    );
  };

  // Handle SMS opt-in
  const handleSmsOptIn = async () => {
    if (!userInfo || !userPhone) {
      setOptInMessage({
        type: "error",
        text: t("notifications.sms.noUserInfo"),
      });
      return;
    }

    showToastWithAction(
      t("notifications.sms.optInConfirm"),
      async () => {
        setIsOptingIn(true);
        setOptInMessage(null);
        setVerificationMessage(null);
        setOptOutMessage(null);

        try {
          // Call backend opt-in endpoint - it will check phone verification status
          const response = await SmsConsentAPI.optInSmsConsent(userInfo.uuid);

          if (response && response.success) {
            if (response.verified) {
              // Phone already verified - consent activated immediately
              setOptInMessage({
                type: "success",
                text:
                  response.message ||
                  t("notifications.sms.enabledFor").replace(
                    "{phone}",
                    userPhone,
                  ),
              });

              // Update consent status to active
              setSmsConsentStatus({
                hasConsent: true,
                verified: true,
                status: "ACTIVE",
                active: true,
                phoneNumber: userPhone,
                consentTimestamp: new Date().toISOString(),
              });

              setShowVerificationInput(false);
            } else {
              // Phone not verified - SMS sent
              setOptInMessage({
                type: "success",
                text:
                  response.message ||
                  t("notifications.sms.verifyDesc").replace(
                    "{phone}",
                    userPhone,
                  ),
              });

              // Update consent status to pending
              setSmsConsentStatus({
                hasConsent: true,
                verified: false,
                status: "PENDING",
                active: false,
                phoneNumber: userPhone,
                consentTimestamp: new Date().toISOString(),
              });

              // Show verification input section
              setShowVerificationInput(true);
            }
          } else {
            setOptInMessage({
              type: "error",
              text: t("notifications.sms.optInFailed"),
            });
          }
        } catch (error) {
          console.error("Error opting in to SMS:", error);

          const errorMessage =
            error.response?.data?.message || error.message || "";

          setOptInMessage({
            type: "error",
            text: errorMessage || t("notifications.sms.optInError"),
          });
        } finally {
          setIsOptingIn(false);
        }
      },
      5,
    );
  };

  // Handle SMS verification
  const handleVerifySms = async () => {
    if (!verificationCode.trim()) {
      setVerificationMessage({
        type: "error",
        text: t("notifications.sms.verifyRequired"),
      });
      return;
    }

    if (!userInfo || !userInfo.uuid) {
      setVerificationMessage({
        type: "error",
        text: t("notifications.sms.noUserInfoRefresh"),
      });
      return;
    }

    setIsVerifying(true);
    setVerificationMessage(null);

    try {
      // Verify the code with backend - updates both users and sms_consents tables
      const response = await SmsConsentAPI.verifySmsConsent(
        userPhone,
        verificationCode.trim(),
        userInfo.uuid,
      );

      if (response && response.success && response.verified) {
        setVerificationMessage({
          type: "success",
          text: t("notifications.sms.verifiedSuccess"),
        });

        // Update consent status to verified and active
        setSmsConsentStatus({
          hasConsent: true,
          verified: true,
          status: "ACTIVE",
          active: true,
          phoneNumber: userPhone,
          consentTimestamp: new Date().toISOString(),
        });

        // Hide verification input
        setShowVerificationInput(false);
        setVerificationCode("");
        setOptInMessage(null); // Clear any previous opt-in messages
      } else {
        setVerificationMessage({
          type: "error",
          text: response.message || t("notifications.sms.verifyFailed"),
        });
      }
    } catch (error) {
      console.error("Error verifying SMS code:", error);
      setVerificationMessage({
        type: "error",
        text:
          error.response?.data?.message || t("notifications.sms.verifyFailed"),
      });
    } finally {
      setIsVerifying(false);
    }
  };

  const isSmsConsentActive =
    smsConsentStatus &&
    smsConsentStatus.hasConsent &&
    smsConsentStatus.verified &&
    smsConsentStatus.status === "ACTIVE";

  // Debug logging
  console.log("[NotificationSettings] SMS Consent Status:", smsConsentStatus);
  console.log("[NotificationSettings] Is Active:", isSmsConsentActive);
  console.log(
    "[NotificationSettings] Has Consent:",
    smsConsentStatus?.hasConsent,
  );
  console.log("[NotificationSettings] Verified:", smsConsentStatus?.verified);
  console.log("[NotificationSettings] Status:", smsConsentStatus?.status);

  return (
    <>
      <div className="common-form-group">
        <h4>{t("notifications.email.title")}</h4>
        <div className="common-toggle-group indented-below-h4">
          <div className="common-toggle-info">
            <h4>{t("notifications.email.securityTitle")}</h4>
            <p>{t("notifications.email.securityDesc")}</p>
          </div>
          <div
            className={`common-toggle ${emailSecurityAlerts ? "active" : ""}`}
            onClick={() =>
              handleToggle(
                "emailSecurityAlerts",
                emailSecurityAlerts,
                setEmailSecurityAlerts,
              )
            }
            role="switch"
            aria-checked={emailSecurityAlerts}
          ></div>
        </div>
        <div className="common-toggle-group indented-below-h4">
          <div className="common-toggle-info">
            <h4>{t("notifications.email.trainingTitle")}</h4>
            <p>{t("notifications.email.trainingDesc")}</p>
          </div>
          <div
            className={`common-toggle ${emailTrainingReminders ? "active" : ""}`}
            onClick={() =>
              handleToggle(
                "emailTrainingReminders",
                emailTrainingReminders,
                setEmailTrainingReminders,
              )
            }
            role="switch"
            aria-checked={emailTrainingReminders}
          ></div>
        </div>
        <div className="common-toggle-group indented-below-h4">
          <div className="common-toggle-info">
            <h4>{t("notifications.email.weeklyTitle")}</h4>
            <p>{t("notifications.email.weeklyDesc")}</p>
          </div>
          <div
            className={`common-toggle ${emailWeeklyDigest ? "active" : ""}`}
            onClick={() =>
              handleToggle(
                "emailWeeklyDigest",
                emailWeeklyDigest,
                setEmailWeeklyDigest,
              )
            }
            role="switch"
            aria-checked={emailWeeklyDigest}
          ></div>
        </div>
      </div>

      <div className="common-form-group">
        <h4>{t("notifications.sms.title")}</h4>

        {isLoadingConsent ? (
          <div className="notif-loading-box">
            <LoadingSpinner
              size="sm"
              message={t("notifications.sms.loading")}
              className="compact"
            />
          </div>
        ) : (
          <>
            <div className="common-toggle-group indented-below-h4">
              <div className="common-toggle-info">
                <h4>{t("notifications.sms.consentTitle")}</h4>
                <p>
                  {isSmsConsentActive
                    ? t("notifications.sms.enabledFor").replace(
                        "{phone}",
                        userPhone,
                      )
                    : t("notifications.sms.notOptedIn")}
                </p>
              </div>
              <div
                className={`common-toggle ${
                  isSmsConsentActive ? "active" : ""
                }`}
              ></div>
            </div>

            {/* Show message when user doesn't have a phone number */}
            {!userPhone && (
              <div className="notif-warning-box">
                <FontAwesomeIcon icon={faExclamationCircle} />
                {t("notifications.sms.noPhone")}
              </div>
            )}

            {/* Show Opt-In button when user hasn't opted in */}
            {!isSmsConsentActive && userPhone && (
              <div className="common-toggle-group notif-action-separator indented-below-h4">
                <div className="common-toggle-info">
                  <h4>{t("notifications.sms.enableTitle")}</h4>
                  <p style={{ marginBottom: "0.75rem" }}>
                    {t("notifications.sms.enableDesc").replace(
                      "{phone}",
                      userPhone,
                    )}
                  </p>
                  <button
                    onClick={handleSmsOptIn}
                    disabled={isOptingIn}
                    className="notif-action-btn btn-success"
                  >
                    {isOptingIn ? (
                      <>
                        <FontAwesomeIcon icon={faSpinner} spin />{" "}
                        {t("notifications.sms.processing")}
                      </>
                    ) : (
                      t("notifications.sms.optInButton")
                    )}
                  </button>
                </div>
              </div>
            )}

            {/* Show success/error message for opt-in */}
            {optInMessage && (
              <div
                className={`notif-message-box ${
                  optInMessage.type === "success" ? "success" : "error"
                }`}
              >
                <FontAwesomeIcon
                  icon={
                    optInMessage.type === "success"
                      ? faCheckCircle
                      : faExclamationCircle
                  }
                />
                {optInMessage.text}
              </div>
            )}

            {/* Show verification input when user has opted in but not verified */}
            {showVerificationInput && !isSmsConsentActive && (
              <div className="common-toggle-group notif-action-separator indented-below-h4">
                <div className="common-toggle-info">
                  <h4>{t("notifications.sms.verifyTitle")}</h4>
                  <p style={{ marginBottom: "0.75rem" }}>
                    {t("notifications.sms.verifyDesc").replace(
                      "{phone}",
                      userPhone,
                    )}
                  </p>

                  {verificationMessage && (
                    <div
                      className={`notif-message-box ${
                        verificationMessage.type === "success"
                          ? "success"
                          : "error"
                      }`}
                      style={{ marginBottom: "1rem", marginTop: 0 }}
                    >
                      {verificationMessage.text}
                    </div>
                  )}

                  <div style={{ marginBottom: "0.75rem" }}>
                    <label
                      htmlFor="verificationCode"
                      className="notif-verify-label"
                    >
                      {t("notifications.sms.verificationCodeLabel")}
                    </label>
                    <input
                      type="text"
                      id="verificationCode"
                      value={verificationCode}
                      onChange={(e) => setVerificationCode(e.target.value)}
                      placeholder={t(
                        "notifications.sms.verificationCodePlaceholder",
                      )}
                      maxLength="6"
                      className="notif-verify-input"
                    />
                  </div>

                  <button
                    onClick={handleVerifySms}
                    disabled={isVerifying || !verificationCode.trim()}
                    className="notif-action-btn btn-success"
                  >
                    {isVerifying ? (
                      <>
                        <FontAwesomeIcon icon={faSpinner} spin />{" "}
                        {t("notifications.sms.verifying")}
                      </>
                    ) : (
                      t("notifications.sms.verifyButton")
                    )}
                  </button>
                </div>
              </div>
            )}

            {/* Show Opt-Out button when user has opted in */}
            {isSmsConsentActive && (
              <div className="common-toggle-group notif-action-separator indented-below-h4">
                <div className="common-toggle-info">
                  <h4>{t("notifications.sms.optOutTitle")}</h4>
                  <p style={{ marginBottom: "0.75rem" }}>
                    {t("notifications.sms.optOutDesc")}
                  </p>
                  <button
                    onClick={handleSmsOptOut}
                    disabled={isOptingOut}
                    className="notif-action-btn btn-danger"
                  >
                    {isOptingOut ? (
                      <>
                        <FontAwesomeIcon icon={faSpinner} spin />{" "}
                        {t("notifications.sms.processing")}
                      </>
                    ) : (
                      t("notifications.sms.optOutButton")
                    )}
                  </button>
                </div>
              </div>
            )}

            {optOutMessage && (
              <div
                className={`notif-message-box ${
                  optOutMessage.type === "success" ? "success" : "error"
                }`}
              >
                <FontAwesomeIcon
                  icon={
                    optOutMessage.type === "success"
                      ? faCheckCircle
                      : faExclamationCircle
                  }
                />
                {optOutMessage.text}
              </div>
            )}

            <div className="common-toggle-group indented-below-h4">
              <div className="common-toggle-info">
                <h4>{t("notifications.sms.criticalTitle")}</h4>
                <p>
                  {isSmsConsentActive
                    ? t("notifications.sms.criticalActiveDesc")
                    : t("notifications.sms.criticalInactiveDesc")}
                </p>
              </div>
              <div
                className={`common-toggle ${
                  isSmsConsentActive ? "active" : ""
                }`}
              ></div>
            </div>
          </>
        )}
      </div>

      <div className="common-form-group">
        <h4>{t("notifications.inapp.title")}</h4>
        <div className="common-toggle-group indented-below-h4">
          <div className="common-toggle-info">
            <h4>{t("notifications.inapp.desktopTitle")}</h4>
            <p>{t("notifications.inapp.desktopDesc")}</p>
          </div>
          <div
            className={`common-toggle ${pushNotificationsEnabled ? "active" : ""}`}
            onClick={() =>
              handleToggle(
                "pushNotificationsEnabled",
                pushNotificationsEnabled,
                setPushNotificationsEnabled,
              )
            }
            role="switch"
            aria-checked={pushNotificationsEnabled}
          ></div>
        </div>
      </div>

      <div className="common-form-group">
        <h4>{t("notifications.announcements.title")}</h4>
        <div className="common-form-row indented-below-h4">
          <div className="common-form-field">
            <label htmlFor="alert-level">
              {t("notifications.announcements.levelLabel")}
            </label>
            <select
              id="alert-level"
              value={alertLevel}
              onChange={handleAlertLevelChange}
            >
              <option value="none">
                {t("notifications.announcements.warningsOnly")}
              </option>
              <option value="info">
                {t("notifications.announcements.plusInfo")}
              </option>
              <option value="success">
                {t("notifications.announcements.plusSuccess")}
              </option>
              <option value="both">
                {t("notifications.announcements.showAll")}
              </option>
            </select>

            <div className="alert-guide-container">
              <div className="alert-guide-important">
                <strong>{t("notifications.announcements.noteLabel")}</strong>{" "}
                {t("notifications.announcements.noteText")}
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default NotificationCommon;
